import React from 'react'
import type { Theme, WithStyles } from '@material-ui/core'
import { createStyles, withStyles } from '@material-ui/core'
import type { OptionsDocument, QuoteDocument, RatesDocument, UserPreferences } from 'paintscout'
import type { WithDialogStackContext } from '@paintscout/ui'
import {
  KeypadInputField,
  FormSection,
  InputField,
  Collapse,
  Grid,
  Button,
  OldTileList as TileList,
  Hidden
} from '@paintscout/ui'
import SearchIcon from '@material-ui/icons/Search'

import clone from 'lodash/clone'
import findIndex from 'lodash/findIndex'
import type { EditableRate, RateSectionOption } from '@paintscout/util/builder'
import { getObjectLabels, createRate, getRates, getRateSections } from '@paintscout/util/builder'
import { useFlags } from 'shared/components/common/FlagsProvider'

export interface RateSelectorProps extends WithStyles<typeof styles>, WithDialogStackContext {
  rates?: RatesDocument
  options: OptionsDocument
  quote: QuoteDocument
  rateSection?: string
  quoteType?: string
  selectedRate?: EditableRate
  preferences?: UserPreferences
  canDeactivate?: boolean

  onChange?: (event: any, rate: EditableRate, fields?: any) => void
  onComplete?: (event: any) => void
  onViewStateChange?: (viewingCategories: boolean) => void
  triggerBackToCategories?: number
}

export interface RateSelectorState {
  selectedRateSectionIndex?: number
  selectedRateIndex?: number
  selectedRate?: EditableRate
  rates?: EditableRate[]
  rateSections?: RateSectionOption[]
  fields: any
  rateQuery: string
  sectionQuery: string
  smartTemplate: boolean
}

export const styles = (theme: Theme) => {
  const { pxToRem } = theme.typography

  return createStyles({
    root: {},
    smallTileButton: {
      ['@media (max-width: 450px)']: {
        marginLeft: pxToRem(10),
        minWidth: pxToRem(150)
      }
    },
    formSection: {
      padding: '1.5rem 0'
    },
    form: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center'
    },
    inputField: {
      paddingBottom: pxToRem(30)
    },
    searchAdornment: {
      width: 24,
      height: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    searchGrid: {
      marginBottom: '0.5em'
    },
    customButton: {
      background: theme.palette.common.white,
      color: theme.paintscout.palette.primary,
      border: `1px solid ${theme.paintscout.palette.primary}`,
      whiteSpace: 'nowrap'
    },
    hideChangeButton: {
      display: 'none !important'
    },
    tileWrapper: {
      justifyContent: 'start'
    }
  })
}

class RateSelector extends React.PureComponent<RateSelectorProps, RateSelectorState> {
  constructor(props: RateSelectorProps) {
    super(props)

    const { options, quote, quoteType, rateSection, rates: ratesDocument, canDeactivate } = props

    const smartTemplate = quote?.smartTemplate && quote.type === 'quoteTemplate'
    const rateSections = getRateSections({ options, quote, quoteType, canDeactivate })
    const selectedRateSectionIndex = findIndex(rateSections, { key: rateSection })
    const rates = rateSection
      ? getRates({ options, rates: ratesDocument, rateSection, quoteType: null, canDeactivate })
      : []

    this.state = {
      smartTemplate,
      selectedRateSectionIndex,
      selectedRateIndex: null,
      selectedRate: null,
      rates,
      rateSections,
      fields: {},
      rateQuery: '',
      sectionQuery: ''
    }
  }

  public render() {
    const { classes } = this.props

    return (
      <div className={classes.root}>
        {this.renderForm()}
        {this.renderRateTiles()}
        {this.renderRateSectionTiles()}
      </div>
    )
  }

  public renderRateSectionTiles() {
    const { rateSections, selectedRateSectionIndex, sectionQuery, smartTemplate } = this.state
    const { classes, options } = this.props
    const objectLabels = getObjectLabels({ options })
    const filteredRateSections = rateSections.filter(
      (section: RateSectionOption) => section.label.toLowerCase().indexOf(sectionQuery?.toLowerCase()) !== -1
    )

    return (
      <FormSection classes={{ root: classes.formSection }} hideDivider={true}>
        {!this.state.selectedRateSectionIndex !== null && !this.state.rates.length && (
          <Grid className={classes.searchGrid} container={true} spacing={2} alignItems={'center'}>
            <Grid item={true} xs>
              <InputField
                id="query"
                data-testid="item-search-input"
                placeholder="Search surface categories"
                fullWidth={true}
                value={sectionQuery}
                onChange={(ev) => this.setState({ sectionQuery: ev.currentTarget.value })}
                startAdornment={
                  <div className={classes.searchAdornment}>
                    <SearchIcon />
                  </div>
                }
              />
            </Grid>
            {!smartTemplate && (
              <Grid item={true} xs={'auto'}>
                <Button
                  className={classes.customButton}
                  variant={'dialog'}
                  onClick={(event: any) => {
                    this.handleRateSectionChange(event, null, true)
                  }}
                >
                  <Hidden smDown={true}>+ Custom Rate</Hidden>
                  <Hidden mdUp={true}>Custom</Hidden>
                </Button>
              </Grid>
            )}
          </Grid>
        )}
        <TileList
          classes={{
            changeButton: `${classes.smallTileButton} ${classes.hideChangeButton}`,
            tileWrapper: classes.tileWrapper
          }}
          title={`${objectLabels.substrate.value} Category`}
          items={filteredRateSections}
          selectedIndex={selectedRateSectionIndex}
          onChange={this.handleRateSectionChange}
          allowCustom={false}
          allowChange={true}
        />
      </FormSection>
    )
  }

  public renderRateTiles() {
    const { classes } = this.props
    const { selectedRate, selectedRateIndex, rateQuery, rates, smartTemplate } = this.state
    const filteredRates = rates.filter((rate) => rate.label.toLowerCase().indexOf(rateQuery?.toLowerCase()) !== -1)
    const selectedRateType = selectedRate?.rateType

    return (
      <Collapse show={this.state.selectedRateSectionIndex !== null && !!this.state.rates.length}>
        <FormSection classes={{ root: classes.formSection }} hideDivider={true}>
          {selectedRateIndex === null && selectedRateType !== 'quantity' && (
            <Grid className={classes.searchGrid} container={true} spacing={2} alignItems={'center'}>
              <Grid item={true} xs>
                <InputField
                  id="query"
                  data-testid="item-search-input"
                  placeholder="Search rates"
                  fullWidth={true}
                  value={rateQuery}
                  onChange={(ev) => this.setState({ rateQuery: ev.currentTarget.value })}
                  startAdornment={
                    <div className={classes.searchAdornment}>
                      <SearchIcon />
                    </div>
                  }
                />
              </Grid>
              {!smartTemplate && (
                <Grid item={true} xs={'auto'}>
                  <Button
                    className={classes.customButton}
                    variant={'dialog'}
                    onClick={(event: any) => {
                      this.handleRateChange(event, null, true)
                    }}
                  >
                    <Hidden smDown={true}>+ Custom Rate</Hidden>
                    <Hidden mdUp={true}>Custom</Hidden>
                  </Button>
                </Grid>
              )}
            </Grid>
          )}
          <TileList
            classes={{
              changeButton: `${classes.smallTileButton} ${classes.hideChangeButton}`,
              tileWrapper: classes.tileWrapper
            }}
            title={'Rate'}
            items={filteredRates}
            subLabelField={'caption'}
            onChange={this.handleRateChange}
            selectedIndex={selectedRateIndex}
            showUnselected={selectedRateIndex === null && selectedRateType !== 'quantity'}
            allowCustom={false}
            allowChange={true}
          />
        </FormSection>
      </Collapse>
    )
  }

  public renderForm() {
    const { classes } = this.props

    if (!this.state.selectedRate) {
      return null
    }

    const { rateType, calculationType } = this.state.selectedRate

    return (
      <FormSection classes={{ root: [classes.formSection, classes.form].join(' ') }} hideDivider={true}>
        <Grid container justifyContent="center">
          {calculationType !== 'hours' && rateType === 'quantity' && (
            <Grid item xs={12} sm={6} md={3}>
              <KeypadInputField
                label={'Quantity'}
                format={'quantity'}
                name={'quantity'}
                value={this.state.fields.quantity}
                onChange={this.handleFormChange}
                allowDecimal={true}
                autoFocus={true}
                onKeyPress={this.handleKeyPress}
              />
            </Grid>
          )}
          {calculationType === 'hours' && (
            <Grid item xs={12} sm={6} md={3}>
              <KeypadInputField
                label={'Hours'}
                format={'hours'}
                name={'hours'}
                value={this.state.fields.hours}
                onChange={this.handleFormChange}
                allowDecimal={true}
                autoFocus={true}
                onKeyPress={this.handleKeyPress}
              />
            </Grid>
          )}
          {calculationType === 'price' && (
            <Grid item xs={12} sm={6} md={3}>
              <KeypadInputField
                label={'Price'}
                format={'price'}
                name={'price'}
                value={this.state.fields.price}
                onChange={this.handleFormChange}
                allowDecimal={true}
                autoFocus={true}
                onKeyPress={this.handleKeyPress}
              />
            </Grid>
          )}
        </Grid>
      </FormSection>
    )
  }

  public handleRateSectionChange = (event: any, index: number, custom: boolean) => {
    if (custom) {
      if (this.props.onChange) {
        const { sectionQuery } = this.state
        const rate = { ...createRate({ custom: true }), label: sectionQuery ?? '' }
        this.props.onChange(event, rate)
      }
      return
    }

    if (index === null) {
      this.setState({
        selectedRateSectionIndex: null,
        rates: [],
        selectedRate: null,
        selectedRateIndex: null
      })
      if (this.props.onViewStateChange) {
        this.props.onViewStateChange(true) // Now viewing categories
      }
      return
    }

    const { options, rates: ratesDocument, canDeactivate } = this.props
    const { rateSections, rateQuery, sectionQuery } = this.state
    // Filter rate sections to ensure correct index
    const filteredRateSections = rateSections.filter(
      (section: RateSectionOption) => section.label.toLowerCase().indexOf(sectionQuery?.toLowerCase()) !== -1
    )
    const rateSection = filteredRateSections[index].key
    const rates = getRates({ options, rates: ratesDocument, rateSection, quoteType: null, canDeactivate })
    const filteredRates = rates.filter((rate) => rate.label.toLowerCase().indexOf(rateQuery?.toLowerCase()) !== -1)

    // This is a "single rate" situation - so we'll actually just choose
    if (filteredRates.length === 1) {
      const newRate = clone(filteredRates[0])
      const { rateType, calculationType } = newRate

      if (rateType !== 'quantity' && calculationType !== 'hours') {
        this.setState({
          selectedRateSectionIndex: index,
          selectedRateIndex: 0,
          selectedRate: newRate,
          rates: []
        })
        if (this.props.onChange) {
          this.props.onChange(event, newRate)
        }
      } else {
        const updatedFields = { ...this.state.fields }
        if (calculationType === 'hours' && newRate.values[newRate.defaultApplications - 1]) {
          updatedFields.hours = newRate.values[newRate.defaultApplications - 1]
        }
        if (calculationType === 'price' && newRate.values[0]) {
          updatedFields.price = newRate.values[0]
        }

        this.setState({
          selectedRateSectionIndex: index,
          selectedRateIndex: 0,
          selectedRate: newRate,
          rates: filteredRates,
          fields: updatedFields
        })
        if (this.props.onChange) {
          this.props.onChange(event, newRate, updatedFields)
        }
      }
      return null
    }

    this.setState({
      selectedRateSectionIndex: index,
      rates,
      selectedRate: null
    })
    if (this.props.onViewStateChange) {
      this.props.onViewStateChange(false) // Now viewing rates, not categories
    }
  }

  public handleRateChange = (event: any, index: number, custom: boolean) => {
    if (custom) {
      if (this.props.onChange) {
        const { rateQuery } = this.state
        const rate = { ...createRate({ custom: true }), label: rateQuery ?? '' }
        this.props.onChange(event, rate)
      }
      return
    }

    const { rates: ratesDocument } = this.state
    const { rateQuery } = this.state
    // Filter rates to ensure correct index
    const filteredRates = ratesDocument.filter(
      (rate) => rate.label.toLowerCase().indexOf(rateQuery?.toLowerCase()) !== -1
    )
    const rate = clone(filteredRates[index])

    if (!rate) {
      this.setState({
        selectedRateIndex: null,
        selectedRate: null
      })
      return
    }

    const { rateType, calculationType } = rate

    if (rateType !== 'quantity' && calculationType !== 'hours' && calculationType !== 'price') {
      this.setState({
        selectedRateSectionIndex: null,
        selectedRateIndex: null,
        rates: []
      })

      if (this.props.onChange) {
        this.props.onChange(event, rate)
      }
    } else {
      const updatedFields = {
        ...this.state.fields
      }
      if (calculationType === 'hours' && rate.values[rate.defaultApplications - 1]) {
        updatedFields.hours = rate.values[rate.defaultApplications - 1]
      }
      if (calculationType === 'price' && rate.values[0]) {
        updatedFields.price = rate.values[0]
      }
      this.setState({
        selectedRateIndex: index,
        selectedRate: rate,
        fields: updatedFields
      })

      if (this.props.onChange) {
        this.props.onChange(event, rate, updatedFields)
      }
    }
  }

  public handleOverridableChange = (name: string) => (value: number) => {
    const { selectedRate, fields } = this.state

    const updatedFields = {
      ...fields,
      [name]: {
        default: value
      }
    }

    this.setState({
      fields: updatedFields
    })

    if (this.props.onChange) {
      this.props.onChange(null, selectedRate, updatedFields)
    }
  }

  public handleFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value)
    const name = event.target.name

    const { selectedRate, fields } = this.state

    const updatedFields = {
      ...fields,
      [name]: value
    }

    this.setState({
      fields: updatedFields
    })

    if (this.props.onChange) {
      this.props.onChange(event, selectedRate, updatedFields)
    }
  }

  public handleKeyPress = (event: any) => {
    if (this.props.onComplete && event.key === 'Enter') {
      this.props.onComplete(event)
    }
  }

  public goBackToCategories = () => {
    this.setState({
      selectedRateSectionIndex: null,
      rates: [],
      selectedRate: null,
      selectedRateIndex: null,
      fields: {}
    })
    if (this.props.onViewStateChange) {
      this.props.onViewStateChange(true)
    }
  }

  public componentDidUpdate(prevProps: RateSelectorProps, prevState: RateSelectorState) {
    // Notify parent when view state changes between categories and rates
    if (this.props.onViewStateChange) {
      const wasViewingRates = prevState.selectedRateSectionIndex !== null
      const isViewingRates = this.state.selectedRateSectionIndex !== null
      if (wasViewingRates !== isViewingRates) {
        this.props.onViewStateChange(!isViewingRates)
      }
    }
    // Handle trigger to go back to categories
    if (
      this.props.triggerBackToCategories !== undefined &&
      this.props.triggerBackToCategories !== prevProps.triggerBackToCategories &&
      this.props.triggerBackToCategories > 0
    ) {
      this.goBackToCategories()
    }
  }
}

const StyledRateSelector = withStyles(styles)(RateSelector)

function RateSelectorWithFlags(props: Omit<RateSelectorProps, 'canDeactivate' | 'classes'>) {
  const { canDeactivateSettingsItems } = useFlags()
  return <StyledRateSelector {...props} canDeactivate={!!canDeactivateSettingsItems} />
}

export default RateSelectorWithFlags
